import { useState, useEffect } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function CoupangRank() {
  const checkAuth = useAuthGuard();
  const [trackers, setTrackers] = useState<any[]>([]);
  const [selectedTracker, setSelectedTracker] = useState<any>(null);
  const [keywords, setKeywords] = useState<string[]>(['', '', '', '', '']);
  const [results, setResults] = useState<any[]>([]);
  const [savedKeywords, setSavedKeywords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'list' | 'add' | 'detail'>('list');
  const [newUrl, setNewUrl] = useState('');
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => { loadTrackers(); }, []);

  const loadTrackers = async () => {
    try {
      const res = await client.get('/coupang');
      setTrackers(res.data.trackers || []);
    } catch {}
  };

  const loadSavedKeywords = async (trackerId: number) => {
    try {
      const res = await client.get(`/coupang/${trackerId}/history`);
      const history = res.data.history || [];
      // 키워드별로 그룹핑
      const kwMap: {[kw: string]: any[]} = {};
      history.forEach((h: any) => {
        if (!kwMap[h.keyword]) kwMap[h.keyword] = [];
        kwMap[h.keyword].push(h);
      });
      const saved = Object.entries(kwMap).map(([keyword, hist]) => ({
        keyword,
        history: hist,
        last_rank: hist[0]?.coupang_rank,
      }));
      setSavedKeywords(saved);
    } catch {}
  };

  const handleAddTracker = async () => {
    if (!newUrl) return setError('URL을 입력해주세요');
    setAdding(true); setError('');
    try {
      await client.post('/coupang', { product_url: newUrl });
      setNewUrl('');
      await loadTrackers();
      setView('list');
    } catch (e: any) {
      setError(e.response?.data?.detail || '등록 실패');
    } finally { setAdding(false); }
  };

  const handleSelectTracker = async (t: any) => {
    setSelectedTracker(t);
    setKeywords(['', '', '', '', '']);
    setResults([]);
    setSavedKeywords([]);
    setView('detail');
    try {
      const res = await client.get(`/coupang/${t.id}/history`);
      const history = res.data.history || [];
      const kwMap: {[kw: string]: any[]} = {};
      history.forEach((h: any) => {
        if (!kwMap[h.keyword]) kwMap[h.keyword] = [];
        kwMap[h.keyword].push(h);
      });
      const saved = Object.entries(kwMap).map(([keyword, hist]) => ({
        keyword, history: hist, last_rank: (hist[0] as any)?.coupang_rank,
      }));
      setSavedKeywords(saved);
      if (saved.length > 0) {
        const kws = saved.map((k: any) => k.keyword);
        const padded = [...kws, ...Array(Math.max(0, 5 - kws.length)).fill('')];
        setKeywords(padded);
      }
    } catch {}
  };

  const handleToggleAutoTrack = async (trackerId: number) => {
    try {
      await client.post(`/coupang/${trackerId}/auto-track`);
      await loadTrackers();
    } catch {}
  };

  const handleDeleteTracker = async (trackerId: number) => {
    if (!window.confirm('정말 삭제하시겠어요?')) return;
    try {
      await client.delete(`/coupang/${trackerId}`);
      await loadTrackers();
    } catch {}
  };

  const doCheck = async (keywordsToCheck: string[], append: boolean = false) => {
    const valid = keywordsToCheck.filter(k => k.trim());
    if (!valid.length) return;
    setLoading(true); setError('');

    if (!append) {
      setResults(valid.map(k => ({ keyword: k, status: 'loading' })));
    } else {
      setResults(prev => {
        const existing = prev.filter(r => !valid.includes(r.keyword));
        return [...existing, ...valid.map(k => ({ keyword: k, status: 'loading' }))];
      });
    }

    try {
      const res = await client.post('/coupang/quick-check', {
        product_url: selectedTracker.product_url,
        keywords: valid
      });
      const resResults = res.data.results || [];
      setResults(prev => prev.map(r => {
        const found = resResults.find((rr: any) => rr.keyword === r.keyword);
        return found ? { ...r, ...found, status: 'done' } : { ...r, status: 'done' }
      }));
      await loadSavedKeywords(selectedTracker.id);
    } catch { setError('조회 실패'); }
    finally { setLoading(false); }
  };

  const handleCheck = () => doCheck(keywords);
  const handleRefreshAll = () => doCheck(savedKeywords.map(sk => sk.keyword), false);
  const handleRefreshOne = (keyword: string) => doCheck([keyword], true);

  const s = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 } as any,
    label: { display: 'block', color: '#8892A4', fontSize: 12, marginBottom: 6 } as any,
    input: { width: '100%', background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 12px', color: '#E8ECF4', fontSize: 14, boxSizing: 'border-box', marginBottom: 12 } as any,
    btn: { background: '#E8192C', border: 'none', borderRadius: 8, padding: '10px 24px', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' } as any,
    btnGhost: { background: 'none', border: '0.5px solid #2A3045', borderRadius: 8, padding: '8px 16px', color: '#8892A4', fontSize: 13, cursor: 'pointer', marginRight: 8 } as any,
    btnBlue: { background: '#1A3A5C', border: 'none', borderRadius: 8, padding: '6px 14px', color: '#378ADD', fontSize: 12, fontWeight: 600, cursor: 'pointer' } as any,
    btnSmall: { background: '#1A0F0F', border: '0.5px solid #3A1A1A', borderRadius: 6, padding: '4px 10px', color: '#E8192C', fontSize: 11, cursor: 'pointer' } as any,
  };

  const rankColor = (rank: number | null) => {
    if (!rank) return '#F56565';
    if (rank <= 3) return '#F6AD55';
    if (rank <= 10) return '#E8192C';
    if (rank <= 30) return '#378ADD';
    return '#8892A4';
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: '#1E2535', border: '0.5px solid #2A3045', borderRadius: 6, padding: '8px 12px' }}>
          <div style={{ color: '#8892A4', fontSize: 11 }}>{label}</div>
          <div style={{ color: '#E8192C', fontSize: 14, fontWeight: 600 }}>{payload[0].value}위</div>
        </div>
      );
    }
    return null;
  };

  const KeywordCard = ({ keyword, history, currentRank, status }: any) => {
    const displayRank = currentRank !== undefined ? currentRank : (history?.length > 0 ? history[0].coupang_rank : null);
    return (
      <div style={s.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>{keyword}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {status !== 'loading' && (
              <button style={s.btnSmall} onClick={() => handleRefreshOne(keyword)} disabled={loading}>갱신</button>
            )}
            {status === 'loading' ? (
              <div style={{ color: '#8892A4', fontSize: 13 }}>조회 중...</div>
            ) : displayRank ? (
              <div style={{ background: '#1A0F0F', borderRadius: 6, padding: '6px 12px', textAlign: 'center' as const }}>
                <div style={{ fontSize: 10, color: '#4A5568' }}>순위</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: rankColor(displayRank) }}>{displayRank}위</div>
              </div>
            ) : status === 'done' ? (
              <div style={{ color: '#F56565', fontSize: 13 }}>순위 밖</div>
            ) : null}
          </div>
        </div>

        {history && history.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <ResponsiveContainer width="100%" height={100}>
              <LineChart data={[...history].reverse().filter((h: any) => h.coupang_rank)}>
                <XAxis dataKey="checked_at" tick={{ fill: '#4A5568', fontSize: 10 }} />
                <YAxis reversed domain={['dataMin - 2', 'dataMax + 2']} tick={{ fill: '#4A5568', fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="coupang_rank" stroke="#E8192C" strokeWidth={2} dot={{ fill: '#E8192C', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {history && history.length > 0 && (
          <div style={{ borderTop: '0.5px solid #2A3045', paddingTop: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              <div style={{ color: '#4A5568', fontSize: 11, paddingBottom: 4 }}>날짜/시간</div>
              <div style={{ color: '#4A5568', fontSize: 11, paddingBottom: 4, textAlign: 'right' as const }}>순위</div>
              {[...history].slice(0, 7).map((h: any, j: number) => (
                <>
                  <div key={`d${j}`} style={{ color: '#8892A4', fontSize: 12, padding: '3px 0' }}>{h.checked_at}</div>
                  <div key={`r${j}`} style={{ color: h.coupang_rank ? rankColor(h.coupang_rank) : '#F56565', fontSize: 12, padding: '3px 0', textAlign: 'right' as const, fontWeight: 600 }}>
                    {h.coupang_rank ? `${h.coupang_rank}위` : '순위밖'}
                  </div>
                </>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div>
        {view === 'list' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>쿠팡 순위 조회</h2>
                <p style={{ color: '#8892A4', fontSize: 12, margin: 0 }}>상품을 등록하고 키워드별 순위를 조회하세요</p>
              </div>
              <button style={s.btn} onClick={() => setView('add')}>+ 상품 등록</button>
            </div>
            {trackers.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center' as const, padding: 40 }}>
                <div style={{ color: '#8892A4', fontSize: 14, marginBottom: 12 }}>등록된 상품이 없어요</div>
                <button style={s.btn} onClick={() => setView('add')}>첫 상품 등록하기</button>
              </div>
            ) : trackers.map(t => (
              <div key={t.id} style={s.card}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  {t.thumbnail ? (
                    <img src={t.thumbnail} alt={t.product_name} style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 80, height: 80, borderRadius: 8, background: '#0F1117', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: '#2A3045', fontSize: 24 }}>🛒</span>
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{t.product_name || `상품 ${t.product_id}`}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' as const }}>
                      <a href={t.product_url} target="_blank" rel="noreferrer"
                        style={{ ...s.btnGhost, textDecoration: 'none', fontSize: 12, padding: '6px 14px' }}>바로가기</a>
                      <button style={{ ...s.btnBlue, fontSize: 12, padding: '6px 14px' }} onClick={() => handleSelectTracker(t)}>분석하기</button>
                      <button style={{ background: t.auto_track ? '#1A0F0F' : '#1A2A1A', border: `0.5px solid ${t.auto_track ? '#3A1A1A' : '#2A3A2A'}`, borderRadius: 8, padding: '6px 14px', color: t.auto_track ? '#E8192C' : '#8892A4', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        onClick={() => handleToggleAutoTrack(t.id)}>
                        {t.auto_track ? '🔴 자동추적 ON' : '자동순위추적'}
                      </button>
                      <button style={{ background: 'none', border: '0.5px solid #4A2A2A', borderRadius: 8, padding: '6px 14px', color: '#F56565', fontSize: 12, cursor: 'pointer' }}
                        onClick={() => handleDeleteTracker(t.id)}>삭제</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {view === 'add' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <button style={s.btnGhost} onClick={() => { setView('list'); setError(''); }}>← 뒤로</button>
              <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: 0 }}>상품 등록</h2>
            </div>
            <div style={s.card}>
              <label style={s.label}>쿠팡 상품 URL</label>
              <input style={s.input} value={newUrl} onChange={e => setNewUrl(e.target.value)}
                placeholder="https://www.coupang.com/vp/products/..." />
              <p style={{ color: '#8892A4', fontSize: 12, marginBottom: 16 }}>URL만 입력하면 상품 정보를 자동으로 가져와요</p>
              {error && <p style={{ color: '#F56565', fontSize: 13, marginBottom: 12 }}>{error}</p>}
              <button style={s.btn} onClick={handleAddTracker} disabled={adding}>
                {adding ? '정보 수집 중...' : '등록하기'}
              </button>
            </div>
          </>
        )}

        {view === 'detail' && selectedTracker && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <button style={s.btnGhost} onClick={() => { setView('list'); setResults([]); setSavedKeywords([]); }}>← 뒤로</button>
              <div>
                <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 2px' }}>{selectedTracker.product_name}</h2>
              </div>
            </div>

            <div style={s.card}>
              <label style={s.label}>검색 키워드 (최대 10개)</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                {keywords.map((kw, i) => (
                  <input key={i} style={{ ...s.input, margin: 0 }} value={kw}
                    onChange={e => { const u = [...keywords]; u[i] = e.target.value; setKeywords(u); }}
                    placeholder={`키워드 ${i + 1}`} />
                ))}
              </div>
              {keywords.length < 10 && (
                <button style={{ ...s.btnGhost, marginBottom: 12 }} onClick={() => setKeywords([...keywords, ''])}>+ 키워드 추가</button>
              )}
              {error && <p style={{ color: '#F56565', fontSize: 13, marginBottom: 12 }}>{error}</p>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={s.btn} onClick={handleCheck} disabled={loading}>
                  {loading ? '조회 중...' : '순위 조회'}
                </button>
                {savedKeywords.length > 0 && (
                  <button style={{ ...s.btnGhost, background: '#1A2A3A', color: '#378ADD', border: '0.5px solid #2A3A4A' }}
                    onClick={handleRefreshAll} disabled={loading}>전체 갱신</button>
                )}
              </div>
              {loading && <div style={{ background: '#1A1A2A', border: '0.5px solid #2A3045', borderRadius: 6, padding: '10px 14px', marginTop: 12 }}><span style={{ color: '#F6AD55', fontSize: 13 }}>⏳ 키워드 1개당 10~20초 소요됩니다. 키워드가 많을수록 시간이 오래 걸려요.</span></div>}
            </div>

            {results.map((r, i) => (
              <KeywordCard key={`result-${i}`} keyword={r.keyword}
                history={savedKeywords.find(sk => sk.keyword === r.keyword)?.history || []}
                currentRank={r.rank} status={r.status} />
            ))}

            {savedKeywords
              .filter(sk => !results.find(r => r.keyword === sk.keyword))
              .map((sk, i) => (
                <KeywordCard key={`saved-${i}`} keyword={sk.keyword} history={sk.history} status="saved" />
              ))}
          </>
        )}
      </div>
    </Layout>
  );
}
